import { prodLog } from "@v2/shared/lib/logger";
import type { UnifiedPaywallState } from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";

export type CachedPaywallState = {
  version: number;
  fetchedAt: number;
  expiresAt: number;
  state: UnifiedPaywallState;
  source: {
    memberId?: string;
    boardId?: string;
    appId?: string;
  };
};

export interface PaywallStateStorage {
  read(key: string): CachedPaywallState | null;
  write(key: string, snapshot: CachedPaywallState): void;
  delete(key: string): void;
}

export function createSessionPaywallStateStorage(): PaywallStateStorage {
  // AIDEV-NOTE: SessionStorage can be blocked; maintain in-memory fallback per iframe lifecycle
  const memoryFallback = new Map<string, string>();
  let storageAccessFailed = false;

  const resolveSessionStorage = (): Storage | null => {
    if (storageAccessFailed) {
      return null;
    }

    if (typeof window === "undefined") {
      return null;
    }

    try {
      return window.sessionStorage;
    } catch (error) {
      storageAccessFailed = true;
      prodLog("Paywall state storage unavailable - using in-memory fallback", {
        error,
      });
      return null;
    }
  };

  return {
    read(key) {
      try {
        const storage = resolveSessionStorage();
        const raw = storage
          ? storage.getItem(key)
          : memoryFallback.get(key) ?? null;
        if (!raw) return null;
        return JSON.parse(raw) as CachedPaywallState;
      } catch (error) {
        prodLog("Paywall state storage read failed", { error, key });
        return null;
      }
    },
    write(key, snapshot) {
      let serialized: string | null = null;
      try {
        serialized = JSON.stringify(snapshot);
        const storage = resolveSessionStorage();
        if (storage) {
          storage.setItem(key, serialized);
        } else {
          memoryFallback.set(key, serialized);
        }
      } catch (error) {
        prodLog("Paywall state storage write failed", { error, key });
        if (serialized === null) {
          try {
            serialized = JSON.stringify(snapshot);
          } catch {
            serialized = null;
          }
        }
        if (serialized !== null) {
          memoryFallback.set(key, serialized);
        }
      }
    },
    delete(key) {
      try {
        const storage = resolveSessionStorage();
        if (storage) {
          storage.removeItem(key);
        } else {
          memoryFallback.delete(key);
        }
      } catch (error) {
        prodLog("Paywall state storage delete failed", { error, key });
        memoryFallback.delete(key);
      }
    },
  };
}
